import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Bell, X, ShieldAlert, Trash2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { NotificationRecord } from '../services/notificationsApi'
import { listPendingApprovals, type PendingApprovalSummary } from '@/features/chat/services/chatApi'
import { resolveApproval } from '@/features/dashboard/services/dashboardApi'
import { LeadAlertRow } from './LeadAlertRow'
import { QualificationResultCard } from './QualificationResultCard'
import { CampaignPausedAlertRow } from './CampaignPausedAlertRow'
import { ApprovalQueueCard } from './ApprovalQueueCard'

type Tab = 'alerts' | 'actions'

interface NotificationSidePanelProps {
  open: boolean
  onClose: () => void
  workspaceId: string | null
  notifications: NotificationRecord[]
  onDismiss: (id: string) => void
  onStartAction: (notification: NotificationRecord) => void
  onLeadAction: (notification: NotificationRecord, promptText: string) => void
}

/**
 * The notification bell's panel: a right-side drawer (same backdrop +
 * translate-x slide pattern as ConversationList's mobile drawer) with two
 * switchable sections:
 *   - Alerts: unread LEAD_ASSIGNED / PROSPECT_QUALIFICATION_RESULT notifications.
 *   - Actions: the approval queue — GET /api/chat/approvals, resolved through
 *     the same resolveApproval() the Dashboard's Today panel already uses.
 *     Lightweight approve/reject only; the full edit-arguments flow stays
 *     exclusive to the live in-chat ApprovalCard.
 */
export const NotificationSidePanel: React.FC<NotificationSidePanelProps> = ({
  open,
  onClose,
  workspaceId,
  notifications,
  onDismiss,
  onStartAction,
  onLeadAction,
}) => {
  const { currentTheme: t } = useTheme()
  const [tab, setTab] = useState<Tab>('alerts')

  const [approvals, setApprovals] = useState<PendingApprovalSummary[]>([])
  const [approvalsLoading, setApprovalsLoading] = useState(false)
  const [approvalsError, setApprovalsError] = useState<string | null>(null)
  const [decidingId, setDecidingId] = useState<string | null>(null)
  // Slides in on open via a rAF-deferred transform flip (same technique as
  // AutoDismissCloseButton's ring) — starting the transition from the very
  // first paint would just render already-open, with nothing to animate from.
  const [slidIn, setSlidIn] = useState(false)
  useEffect(() => {
    if (!open) {
      setSlidIn(false)
      return
    }
    const raf = requestAnimationFrame(() => setSlidIn(true))
    return () => cancelAnimationFrame(raf)
  }, [open])

  const alerts = notifications.filter(
    (n) =>
      n.status === 'unread' &&
      (n.type === 'LEAD_ASSIGNED' || n.type === 'PROSPECT_QUALIFICATION_RESULT' || n.type === 'CAMPAIGN_SEND_PAUSED'),
  )

  const loadApprovals = React.useCallback(() => {
    if (!workspaceId) return
    setApprovalsLoading(true)
    setApprovalsError(null)
    listPendingApprovals(workspaceId)
      .then((res) => setApprovals(res.approvals ?? []))
      .catch((err) => setApprovalsError(err instanceof Error ? err.message : 'Failed to load approvals'))
      .finally(() => setApprovalsLoading(false))
  }, [workspaceId])

  // Fetched on open and whenever the Actions tab is selected — not polled;
  // this is a queue you check in, not a live feed like the Alerts list.
  useEffect(() => {
    if (open && tab === 'actions') loadApprovals()
  }, [open, tab, loadApprovals])

  if (!open) return null

  const handleDecide = (conversationId: string, action: 'approved' | 'rejected') => {
    setDecidingId(conversationId)
    setApprovalsError(null)
    resolveApproval(conversationId, action)
      .then(() => setApprovals((prev) => prev.filter((a) => a.conversationId !== conversationId)))
      .catch((err) => setApprovalsError(err instanceof Error ? err.message : 'Failed to submit your decision'))
      .finally(() => setDecidingId(null))
  }

  const handleClearAll = () => {
    for (const n of alerts) onDismiss(n.id)
  }

  return ReactDOM.createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          zIndex: 9998,
          opacity: slidIn ? 1 : 0,
          transition: 'opacity 0.25s ease',
        }}
      />
      <div
        className="flex flex-col"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: '520px',
          maxWidth: '94vw',
          backgroundColor: t.secondary,
          borderLeft: `1px solid ${t.border}`,
          boxShadow: '-16px 0 48px rgba(0,0,0,0.25)',
          zIndex: 9999,
          transform: slidIn ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 h-16 shrink-0 border-b"
          style={{ borderColor: t.border }}
        >
          <div className="flex items-center gap-2.5">
            <Bell size={18} style={{ color: t.accent }} />
            <span className="text-base font-semibold" style={{ color: t.text }}>Notifications</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg" style={{ color: t.mutedText }}>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-6 pt-4 gap-2 shrink-0">
          {([
            { key: 'alerts' as const, label: 'Alerts', count: alerts.length },
            { key: 'actions' as const, label: 'Actions', count: approvals.length },
          ]).map((t2) => {
            const isActive = tab === t2.key
            return (
              <button
                key={t2.key}
                type="button"
                onClick={() => setTab(t2.key)}
                className="flex items-center gap-1.5 px-3.5 h-9 rounded-lg text-[13.5px] font-semibold transition-colors"
                style={{
                  backgroundColor: isActive ? t.hoverBackground : 'transparent',
                  color: isActive ? t.text : t.mutedText,
                }}
              >
                {t2.label}
                {t2.count > 0 && (
                  <span
                    className="min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                    style={{ backgroundColor: '#EF4444', color: '#FFFFFF' }}
                  >
                    {t2.count > 9 ? '9+' : t2.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <div className="mx-6 mt-4 border-b" style={{ borderColor: t.border }} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-5 flex flex-col gap-4">
          {tab === 'alerts' ? (
            alerts.length === 0 ? (
              <EmptyState icon={<Bell size={26} style={{ color: t.mutedText, opacity: 0.4 }} />} label="No new alerts" />
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex items-center gap-1.5 self-end text-[12px] font-medium"
                  style={{ color: t.mutedText }}
                >
                  <Trash2 size={12} />
                  Clear all
                </button>
                {alerts.map((n) =>
                  n.type === 'PROSPECT_QUALIFICATION_RESULT' ? (
                    <QualificationResultCard key={n.id} notification={n} onDismiss={onDismiss} onAfterGoToChat={onClose} />
                  ) : n.type === 'CAMPAIGN_SEND_PAUSED' ? (
                    <CampaignPausedAlertRow key={n.id} notification={n} onDismiss={onDismiss} onAfterReconnect={onClose} />
                  ) : (
                    <LeadAlertRow
                      key={n.id}
                      notification={n}
                      onDismiss={onDismiss}
                      onStartAction={(notif) => { onStartAction(notif); onClose() }}
                      onLeadAction={(notif, prompt) => { onLeadAction(notif, prompt); onClose() }}
                    />
                  ),
                )}
              </>
            )
          ) : approvalsLoading ? (
            <EmptyState icon={<ShieldAlert size={26} style={{ color: t.mutedText, opacity: 0.4 }} />} label="Loading…" />
          ) : approvalsError ? (
            <EmptyState icon={<ShieldAlert size={26} style={{ color: t.errorText, opacity: 0.6 }} />} label={approvalsError} />
          ) : approvals.length === 0 ? (
            <EmptyState icon={<ShieldAlert size={26} style={{ color: t.mutedText, opacity: 0.4 }} />} label="Nothing waiting on you" />
          ) : (
            approvals.map((approval) => (
              <ApprovalQueueCard
                key={approval.id}
                approval={approval}
                onDecide={handleDecide}
                deciding={decidingId === approval.conversationId}
              />
            ))
          )}
        </div>
      </div>
    </>,
    document.body,
  )
}

const EmptyState: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => {
  const { currentTheme: t } = useTheme()
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      {icon}
      <p className="text-[13px]" style={{ color: t.mutedText }}>{label}</p>
    </div>
  )
}
